import React, { useState } from "react";
import { FlightOffer } from "../../types/flight";
import {
    formatTime,
    formatDuration,
    getStopsText,
} from "../../utils/flightUtils";
import FareBrandDrawer from "./FareBrandDrawer";

interface FlightOfferRowProps {
    flight: FlightOffer;
    isSelected: boolean;
    onSelect: (flight: FlightOffer, fareType: string) => void;
    selectedFareType?: string;
}

const FlightOfferRow: React.FC<FlightOfferRowProps> = ({
    flight,
    isSelected,
    onSelect,
    selectedFareType,
}) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerFareType, setDrawerFareType] = useState<
        "economy" | "premium" | "business" | null
    >(null);

    const itinerary = flight.itineraries[0];
    const firstSegment = itinerary.segments[0];
    const lastSegment = itinerary.segments[itinerary.segments.length - 1];

    const currency = flight.price.currency;
    const basePrice = parseFloat(flight.price.total);

    const formatPrice = (amount: number): string => {
        return amount.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    };

    // Get the actual cabin class from the API
    const actualCabin = React.useMemo(() => {
        const travelerPricing = flight.travelerPricings[0];
        if (
            travelerPricing &&
            travelerPricing.fareDetailsBySegment.length > 0
        ) {
            return travelerPricing.fareDetailsBySegment[0].cabin;
        }
        return "ECONOMY"; // Default
    }, [flight]);

    // Determine which fare type to show based on cabin class
    const displayFareType = React.useMemo(() => {
        if (actualCabin === "PREMIUM_ECONOMY") {
            return "premium";
        } else if (actualCabin === "BUSINESS" || actualCabin === "FIRST") {
            return "business";
        }
        return "economy";
    }, [actualCabin]);

    // Get the display name for the cabin class
    const getCabinDisplayName = () => {
        switch (displayFareType) {
            case "economy":
                return "Economy";
            case "premium":
                return "Premium Economy";
            case "business":
                return "Business";
            default:
                return "Economy";
        }
    };

    // Get the background color for the single fare column
    const getFareColumnStyle = () => {
        switch (displayFareType) {
            case "economy":
                return "bg-[#e8f4ff]";
            case "premium":
                return "bg-[#f8e4e2]";
            case "business":
                return "bg-[#fff9e6]";
            default:
                return "bg-[#e8f4ff]";
        }
    };

    // Get the top border color
    const getTopBorderColor = () => {
        switch (displayFareType) {
            case "economy":
                return "bg-[#1e70eb]";
            case "premium":
                return "bg-[#e07773]";
            case "business":
                return "bg-[#ff9800]";
            default:
                return "bg-[#1e70eb]";
        }
    };

    const handleFareClick = () => {
        // Toggle drawer if clicking the same fare type
        if (drawerOpen && drawerFareType === displayFareType) {
            setDrawerOpen(false);
            setDrawerFareType(null);
        } else {
            setDrawerFareType(
                displayFareType as "economy" | "premium" | "business"
            );
            setDrawerOpen(true);
        }
    };

    const handleSelectBrand = (brandName: string, price: number) => {
        if (drawerFareType) {
            // Pass the brand name and price along with the selection
            (onSelect as any)(flight, drawerFareType, brandName, price);
            setDrawerOpen(false);
        }
    };

    return (
        <div>
            <div
                className="bg-white rounded-lg shadow-sm overflow-hidden"
                style={{ height: "103px" }}
            >
                <div className="flex h-full">
                    {/* Left Section - 60% width */}
                    <div className="w-3/5 flex">
                        {/* Flight Route Info - Left half */}
                        <div className="w-1/2 p-3 flex flex-col justify-center">
                            <div className="flex items-center w-full">
                                {/* Departure time */}
                                <div className="text-lg font-bold text-black">
                                    {formatTime(firstSegment.departure.at)}
                                </div>

                                {/* Route line with stops */}
                                <div className="flex-1 flex items-center px-3">
                                    <span className="text-[#0047ba] text-2xl">
                                        •
                                    </span>
                                    <div className="flex-1 border-t-2 border-dotted border-gray-400 mx-1"></div>
                                    <span className="text-xs sm text-gray-600 px-2 bg-[#e6edf8] rounded-md">
                                        {getStopsText(itinerary.segments)}
                                    </span>
                                    <div className="flex-1 border-t-2 border-dotted border-gray-400 mx-1"></div>
                                    <span className="text-[#0047ba] text-2xl">
                                        •
                                    </span>
                                </div>

                                {/* Arrival time */}
                                <div className="text-lg font-bold text-black">
                                    {formatTime(lastSegment.arrival.at)}
                                </div>
                            </div>

                            {/* Airport codes below */}
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-700 font-medium">
                                    {firstSegment.departure.iataCode}
                                </div>
                                <div className="text-sm text-gray-700 font-medium">
                                    {lastSegment.arrival.iataCode}
                                </div>
                            </div>
                        </div>

                        {/* Flight Details - Right half */}
                        <div className="w-1/2 p-3 border-l border-gray-200">
                            <div className="flex flex-col justify-center h-full">
                                {/* Duration */}
                                <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                                    <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <span>
                                        {formatDuration(itinerary.duration)}
                                    </span>
                                </div>

                                {/* Airline info */}
                                <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                    <span>
                                        PR {firstSegment.number} operated by
                                        Philippine Airlines
                                    </span>
                                    <img
                                        src="https://content.airhex.com/content/logos/airlines_PR_20_20_s.png"
                                        alt="PR"
                                        className="w-4 h-4"
                                    />
                                </div>

                                {/* See itinerary details link */}
                                <div>
                                    <button className="text-[#0047ba] text-xs underline pl-6">
                                        See itinerary details
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Single Fare Option Section - Right side */}
                    <div className="w-2/5">
                        <div
                            className={`h-full cursor-pointer hover:opacity-90 transition-opacity relative ${getFareColumnStyle()}`}
                            onClick={handleFareClick}
                        >
                            <div className={`h-1 ${getTopBorderColor()}`}></div>
                            <div className="p-3">
                                <div className="text-sm font-medium text-gray-900 mb-1">
                                    {getCabinDisplayName()}
                                </div>
                                <div className="text-xs text-gray-600 mb-2">
                                    From {currency}
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="text-lg font-bold text-gray-900">
                                        {formatPrice(basePrice)}
                                    </div>
                                    <svg
                                        className="w-6 h-6"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d={
                                                drawerOpen
                                                    ? "M5 15l7-7 7 7"
                                                    : "M19 9l-7 7-7-7"
                                            }
                                            stroke="black"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fare Brand Drawer - appears inline below the flight card */}
            {drawerOpen && drawerFareType && (
                <div className="overflow-hidden shadow-sm">
                    <FareBrandDrawer
                        flight={flight}
                        fareType={drawerFareType}
                        onSelectBrand={handleSelectBrand}
                        onClose={() => setDrawerOpen(false)}
                    />
                </div>
            )}
        </div>
    );
};

export default FlightOfferRow;
